export function normaliseAddress(postcode, province, suburb, street) {
  const zip = String(postcode ?? '').trim();
  const provinces = new Set(['Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia']);
  const city = String(suburb ?? '').trim();
  const address1 = String(street ?? '').trim();
  if (!/^\d{4}$/.test(zip) || !provinces.has(province) || !city || !address1) throw new Error('address');
  return { zip, province, city, address1, country: 'Australia' };
}

export function cartFingerprint(cart) {
  return [cart.currency, cart.total_price, ...cart.items.map((item) => `${item.key}:${item.quantity}:${item.final_line_price}`)].join('|');
}

async function readResponse(response) {
  if (!response.ok) throw new Error(response.status === 422 ? 'address' : 'unavailable');
  const body = await response.text();
  if (!body.trim()) return null;
  try { return JSON.parse(body); } catch { throw new Error('unavailable'); }
}

function pause(duration, signal) {
  return new Promise((resolve, reject) => {
    signal?.throwIfAborted();
    const cancel = () => { clearTimeout(timer); reject(signal.reason); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', cancel); resolve(); }, duration);
    signal?.addEventListener('abort', cancel, { once: true });
  });
}

export async function shippingRates(cartUrl, address, { signal, fetcher = fetch, interval = 600, attempts = 20 } = {}) {
  const base = cartUrl.replace(/\/$/, '');
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(address)) params.set(`shipping_address[${key}]`, value);
  await readResponse(await fetcher(`${base}/prepare_shipping_rates.json`, {
    method: 'POST', credentials: 'same-origin', signal,
    headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body: params.toString()
  }));
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const result = await readResponse(await fetcher(`${base}/async_shipping_rates.json?${params}`, {
      credentials: 'same-origin', signal, cache: 'no-store', headers: { Accept: 'application/json' }
    }));
    if (Array.isArray(result?.shipping_rates)) {
      return result.shipping_rates.map((rate) => {
        const validPrice = typeof rate.price === 'number' || (typeof rate.price === 'string' && rate.price.trim() !== '');
        const price = validPrice ? Number(rate.price) : NaN;
        if (!Number.isFinite(price) || price < 0 || !rate.name) throw new Error('unavailable');
        return { name: String(rate.presentment_name || rate.name), price };
      });
    }
    if (attempt + 1 < attempts) await pause(interval, signal);
  }
  throw new Error('unavailable');
}

let lastAddress = { zip: '', province: '', city: '', address1: '' };

if (typeof HTMLElement !== 'undefined' && !customElements.get('delaceys-shipping-estimator')) {
  class DelaceysShippingEstimator extends HTMLElement {
    controller;
    listeners;
    timer;

    connectedCallback() {
      if (this.listeners || this.dataset.blocked === 'true') return;
      this.fields = this.querySelector('[data-estimator-fields]');
      this.postcode = this.querySelector('[data-estimator-postcode]');
      this.province = this.querySelector('[data-estimator-province]');
      this.street = this.querySelector('[data-estimator-street]');
      this.suburb = this.querySelector('[data-estimator-suburb]');
      this.button = this.querySelector('[data-estimator-button]');
      this.status = this.querySelector('[data-estimator-status]');
      this.rates = this.querySelector('[data-estimator-rates]');
      if (!this.fields || !this.postcode || !this.province || !this.street || !this.suburb || !this.button || !this.status || !this.rates) return;
      this.readyLabel = this.button.textContent;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      if (lastAddress.zip) this.postcode.value = lastAddress.zip;
      if (lastAddress.province) this.province.value = lastAddress.province;
      if (lastAddress.city) this.suburb.value = lastAddress.city;
      if (lastAddress.address1) this.street.value = lastAddress.address1;
      this.fields.hidden = false;
      this.button.addEventListener('click', () => this.calculate(), options);
      this.fields.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('input')) { event.preventDefault(); this.calculate(); }
      }, options);
      this.fields.addEventListener('input', () => this.invalidate(), options);
      this.fields.addEventListener('change', () => this.invalidate(), options);
      const refreshAfterCartChange = () => {
        this.invalidate(true);
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.listeners?.abort();
          this.listeners = null;
          this.connectedCallback();
        });
      };
      document.addEventListener('cart:update', refreshAfterCartChange, options);
      document.addEventListener('discount:update', refreshAfterCartChange, options);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.listeners?.abort();
      this.listeners = null;
      clearTimeout(this.timer);
    }

    invalidate(cartChanged = false) {
      if (!cartChanged) lastAddress = { zip: this.postcode.value.trim(), province: this.province.value, city: this.suburb.value.trim(), address1: this.street.value.trim() };
      const hadResult = this.rates.children.length > 0 || this.button.disabled;
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.timer);
      this.button.disabled = false;
      this.button.textContent = this.readyLabel;
      this.removeAttribute('aria-busy');
      this.rates.replaceChildren();
      this.status.textContent = cartChanged && hadResult ? this.dataset.changed : '';
    }

    async cartState(signal) {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store', signal, headers: { Accept: 'application/json' } });
      const cart = await readResponse(response);
      if (!cart || !Array.isArray(cart.items)) throw new Error('unavailable');
      return cart;
    }

    async calculate() {
      if (this.button.disabled) return;
      this.invalidate();
      let address;
      try { address = normaliseAddress(this.postcode.value, this.province.value, this.suburb.value, this.street.value); }
      catch {
        this.status.textContent = this.dataset.addressError;
        (!this.street.value.trim() ? this.street : !this.suburb.value.trim() ? this.suburb : !/^\d{4}$/.test(this.postcode.value.trim()) ? this.postcode : this.province).focus();
        return;
      }
      const controller = new AbortController();
      this.controller = controller;
      this.timer = setTimeout(() => controller.abort(new Error('timeout')), 22000);
      this.button.disabled = true;
      this.button.textContent = this.dataset.loading;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      try {
        const before = await this.cartState(controller.signal);
        if (cartFingerprint(before) !== this.dataset.cartSignature) throw new Error('changed');
        const rates = await shippingRates(this.dataset.cartUrl, address, { signal: controller.signal });
        const after = await this.cartState(controller.signal);
        if (cartFingerprint(before) !== cartFingerprint(after)) throw new Error('changed');
        if (!this.isConnected || this.controller !== controller) return;
        const currency = after.currency || this.dataset.currency || 'AUD';
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency });
        const rows = rates.map((rate) => {
          const row = document.createElement('li');
          const name = document.createElement('span');
          const price = document.createElement('strong');
          name.textContent = rate.name;
          price.textContent = rate.price === 0 ? this.dataset.free : money.format(rate.price);
          row.append(name, price);
          return row;
        });
        this.rates.replaceChildren(...rows);
        this.status.textContent = rates.length ? this.dataset.resultsLabel : this.dataset.noRates;
      } catch (error) {
        if (!this.isConnected || this.controller !== controller) return;
        this.rates.replaceChildren();
        this.status.textContent = error.message === 'address' ? this.dataset.addressError : error.message === 'changed' ? this.dataset.changed : this.dataset.unavailable;
      } finally {
        if (this.controller === controller) {
          clearTimeout(this.timer);
          this.controller = null;
          this.button.disabled = false;
          this.button.textContent = this.readyLabel;
          this.removeAttribute('aria-busy');
        }
      }
    }
  }
  customElements.define('delaceys-shipping-estimator', DelaceysShippingEstimator);
}

